import { useNotificationBody } from "@src/features/app/hooks/useNotificationBody";
import Avatar from "@src/lib/ui/Avatar";
import useIntersectionObserver from "@src/lib/hooks/useIntersectionObserver";
import { formatRelativeDate } from "@src/lib/utils/dates";
import { parseISO } from "date-fns";
import { DropdownMenuItem } from "@src/lib/ui/DropdownMenu";
import Link from "next/link";
import { cn } from "@src/lib/utils/cn";
import { NotificationFragment } from "@src/fragments.generated";
import React from "react";
import NoNotificationsImage from "./NoNotificationsImage";
import Text from "@src/lib/ui/Text";
import Spinner from "@src/lib/ui/Spinner";

const NotificationDot: React.FC<{
  count?: number;
  className?: string;
  size: 12 | 24;
}> = (props) => {
  return (
    <div
      style={{ width: props.size, height: props.size }}
      className={cn(
        `bg-destructive rounded-full`,
        !!props.count && "flex items-center justify-center",
        props.className,
      )}
    >
      {props.count && (
        <Text variant="fine-print-title" className="text-white">
          {props.count}
        </Text>
      )}
    </div>
  );
};

const NotificationRow: React.FC<{
  notification: NotificationFragment;
  listRef: React.RefObject<HTMLElement | null>;
  inViewport: (id: string) => void;
}> = (props) => {
  const rowRef = React.useRef<HTMLDivElement | null>(null);
  const { isInViewport } = useIntersectionObserver({
    root: props.listRef.current,
    target: rowRef,
    threshold: 1.0,
  });
  const [markedAsRead, setMarkedAsRead] = React.useState(false);
  // Visible rows are queued as read without clearing their dot, so the user
  // still sees what's new; hovering a row is an explicit read, so clear it.
  const [hoveredRead, setHoveredRead] = React.useState(false);
  const notificationBody = useNotificationBody(props.notification);
  const { title, body, url, creator } = notificationBody || {};
  const markRead = React.useCallback(() => {
    if (!props.notification.readAt && !markedAsRead) {
      props.inViewport(props.notification.id);
      setMarkedAsRead(true);
    }
  }, [markedAsRead, props]);
  React.useEffect(() => {
    if (isInViewport && props.listRef.current) {
      markRead();
    }
  }, [isInViewport, props.listRef, markRead]);
  if (!notificationBody) return null;
  const Component = (
    <DropdownMenuItem
      ref={rowRef}
      className="flex items-stretch"
      onClick={markRead}
      onPointerMove={(e) => {
        // A real mouse move only, like Radix's own hover: rows scrolling
        // under a still cursor or a finger scrolling the list aren't reads
        // (taps still mark read via onClick).
        if (e.pointerType !== "mouse") return;
        markRead();
        setHoveredRead(true);
      }}
    >
      {creator && (
        <Avatar
          size={40}
          person={{ ...creator, id: creator.id ?? 0 }}
          showTooltip
        />
      )}
      <div className="flex flex-1 flex-col items-start">
        <Text variant="compact-description" multiline>
          {title}
        </Text>
        <Text variant="compact-description" className="mb-1">
          {body}
        </Text>
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          {formatRelativeDate(parseISO(props.notification.createdAt))}
        </Text>
      </div>
      {!props.notification.readAt && !hoveredRead && (
        <div className="flex w-3 items-center justify-end">
          <NotificationDot size={12} />
        </div>
      )}
    </DropdownMenuItem>
  );
  if (url) {
    return (
      <Link href={url} className="w-full">
        {Component}
      </Link>
    );
  }
  return Component;
};

type Props = {
  notifications: NotificationFragment[];
  loading: boolean;
  hasMore: boolean;
  fetchMoreNotifications: () => void;
  onNotificationRead: (id: string) => void;
};

export const NotificationList: React.FC<Props> = (props) => {
  const [fetchedMore, setFetchedMore] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const scrolledPastThreshold =
      target.scrollTop > (target.scrollHeight - target.clientHeight) * 0.7;
    if (scrolledPastThreshold && !fetchedMore) {
      props.fetchMoreNotifications();
      setFetchedMore(true);
    }
  };
  React.useEffect(() => {
    if (fetchedMore) {
      setFetchedMore(false);
    }
  }, [fetchedMore, props.notifications]);
  return (
    <>
      {props.loading && (
        <div className="flex size-full items-center justify-center">
          <Spinner size={64} />
        </div>
      )}
      {props.notifications.length === 0 ? (
        <div className="flex size-full flex-col items-center justify-center">
          <NoNotificationsImage size={200} />
          <Text variant="body-description">You have no notifications</Text>
        </div>
      ) : (
        <div className="flex size-full flex-col">
          <Text variant="body-title" className="mt-2 mb-4 px-2">
            Notifications
          </Text>
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="flex flex-1 flex-col overflow-y-scroll"
          >
            {props.notifications.map((n) => (
              <NotificationRow
                key={n.id}
                listRef={listRef}
                notification={n}
                inViewport={props.onNotificationRead}
              />
            ))}
            {props.hasMore && (
              <div className="flex w-full items-center justify-center py-3">
                <Spinner size={24} />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationList;
